export default function IslamoradaDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Islamorada requests are fishing or marine-related travel with under 3 weeks notice",
    },
    {
      value: "2–3 weeks",
      label: "average notice for international fishing tournaments and charter rotations",
    },
    {
      value: "Nov–Apr",
      label: "peak tournament and fishing season — passport renewals spike before sailfish and offshore events",
    },
    {
      value: "48-hour",
      label: "shortest tournament departure window we've accommodated for an Islamorada captain",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas",
      context: "The closest international destination to Islamorada — Bimini is just 50 miles across the Gulf Stream, making the Bahamas the most common international fishing destination for Keys anglers. Nassau, West End, Grand Bahama, and the Abacos host major tournaments including the Bahamas Wahoo Championship. Islamorada charter captains regularly run clients to Bimini and Walker's Cay for marlin, wahoo, and tuna. Day trips and weekend fishing expeditions to the Bahamas are a staple of Islamorada's offshore culture.",
      urgency: "Bahamas tournament invitations often arrive with 1–2 weeks of notice — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica",
      context: "Costa Rica's Pacific coast — particularly Quepos, Los Suenos, and Golfito — hosts some of the world's premier marlin, sailfish, and dorado fisheries. The Offshore World Championship in Quepos draws international teams including Islamorada-based boats. Costa Rica also offers exceptional diving on both coasts, rainforest eco-tourism, and a laid-back Pura Vida culture that resonates with Keys residents.",
      urgency: "Team slots for Costa Rica tournaments often open with 2–3 weeks of notice when anglers drop out or boats become available",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico — Cabo & Cancun",
      context: "Cabo San Lucas hosts the Bisbee's Black & Blue Marlin Tournament, one of the richest fishing competitions in the world. Cancun and Cozumel offer exceptional reef diving and sportfishing within a short flight from MIA. Mexico requires six months of passport validity for entry. Islamorada families and anglers travel to both destinations for fishing, diving, and beach vacations.",
      urgency: "Spring break and tournament bookings surge in January — passport discoveries spike in February and March for Islamorada travelers",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Caribbean Cruises",
      context: "Islamorada residents regularly cruise from Miami and Fort Lauderdale to the Eastern and Western Caribbean — the Bahamas, Jamaica, the Cayman Islands, Cozumel, the Virgin Islands, and St. Maarten. While Islamorada itself offers world-class marine experiences, cruises provide a different kind of vacation: multiple islands, all-inclusive dining, and family-friendly activities. All cruise passengers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Belize & Central America",
      context: "Belize's barrier reef — the second-largest in the world — draws Islamorada divers and snorkelers for world-class wall diving, blue hole exploration, and marine conservation projects. Panama's Pearl Islands and Coiba National Park attract serious offshore anglers. These destinations appeal to Islamorada's conservation-minded marine community.",
      urgency: "Dive trip bookings and marine research expedition invitations often arrive with 2–4 weeks of notice",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Panama & South America",
      context: "Panama's Pacific coast offers some of the most prolific big-game fishing in the world, with Panama City and the Pearl Islands becoming increasingly popular with Islamorada charter captains. Ecuador's Galapagos Islands draw dive enthusiasts for hammerhead shark encounters and marine iguana snorkeling. These trips represent the cutting edge of marine adventure travel from the Keys.",
      urgency: "Expedition-style trips to Panama and the Galapagos are booked 1–2 months ahead, but passport discoveries often happen during final planning",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Islamorada Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Islamorada
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Islamorada travelers head to the Bahamas for fishing, Costa Rica and Mexico for tournaments, the Caribbean for cruises, and Central America for diving. These are the destinations that create the most passport urgency for our Islamorada clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Key West", href: "/key-west" },
              { name: "Naples", href: "/naples" },
              { name: "Bonita Springs", href: "/bonita-springs" },
              { name: "Marco Island", href: "/marco-island" },
              { name: "Fort Myers", href: "/fort-myers" },
              { name: "Miami", href: "/aventura" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}